import { css } from '@emotion/react'
import { Cell, Grid } from 'bold-ui'
import { Divider } from 'components/divider/Divider'
import { useMedicoesAtendimentosAnterioresQuery } from 'graphql/hooks.generated'
import { AtendimentoProfissional, Medicao, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import {
  getMedicaoForImcReferencia,
  getMedicoesAntropometricasPreNatalSorted,
} from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/util-medicoesPreNatal'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { AcompanhamentoHeaderLink } from '../../components/AcompanhamentoHeaderLink'
import { AcompanhamentoIconLabel } from '../../components/AcompanhamentoIconLabel'
import {
  formatEdema,
  formatGanhoPeso,
  formatGlicemiaCapilar,
  formatMedicaoDefault,
  formatMovimentacaoFetal,
} from '../formatter-acompanhamentoPreNatal'
import { convertToUltimasMedicoesRegistradas, getMedicoesPreNatal } from '../util-acompanhamentoPreNatal'

interface MedicoesGestacaoCardProps {
  prontuarioId: ID
  dataInicioGestacao: Date
  atendimentosProfUltimaGestacao: AtendimentoProfissional[]
  medicoesPath: string
}

export function MedicoesGestacaoCard(props: Readonly<MedicoesGestacaoCardProps>) {
  const { prontuarioId, dataInicioGestacao, atendimentosProfUltimaGestacao, medicoesPath } = props
  const styles = createStyles()

  const {
    data: { medicoes },
    loading: loadingMedicoes,
  } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId },
    fetchPolicy: 'cache-first',
  })

  const medicoesAntropometricasPreNatal = getMedicoesAntropometricasPreNatalSorted(medicoes)
  const medicoesPreNatal = getMedicoesPreNatal(atendimentosProfUltimaGestacao)
  const medicaoForImcReferencia = getMedicaoForImcReferencia(dataInicioGestacao, medicoesAntropometricasPreNatal)

  const ultimasMedicoes = convertToUltimasMedicoesRegistradas(medicoesPreNatal, medicoes as Medicao[])

  return (
    <AcompanhamentoCard
      id='medicoes-gestante'
      title='Medições da gestante'
      tipoAcompanhamento={TipoAcompanhamentoEnum.PRE_NATAL}
      loading={loadingMedicoes}
      placeholder='Nenhuma medição registrada'
      headerContent={<AcompanhamentoHeaderLink title='Visualizar histórico de medições' path={medicoesPath} />}
      justifyHeaderContent='space-between'
    >
      {ultimasMedicoes && (
        <Grid gap={0}>
          <Cell size={4} style={[styles.item, styles.upperItem]}>
            <AcompanhamentoIconLabel
              icon='fundalHeightFilled'
              iconText='Altura uterina'
              childrenText={formatMedicaoDefault(ultimasMedicoes.alturaUterina, 'cm')}
            />
          </Cell>
          <Cell size={4} style={[styles.item, styles.upperItem]}>
            <AcompanhamentoIconLabel
              icon='balancePlus'
              iconText='Ganho de peso'
              childrenText={formatGanhoPeso(dataInicioGestacao, ultimasMedicoes.peso, medicaoForImcReferencia)}
            />
          </Cell>
          <Cell size={4} style={[styles.item, styles.upperItem]}>
            <AcompanhamentoIconLabel
              icon='bloodPressureFilled'
              iconText='Pressão arterial'
              childrenText={formatMedicaoDefault(ultimasMedicoes.pressaoArterial, 'mmHg')}
            />
          </Cell>
          <Cell size={12} style={styles.dividerCell}>
            <Divider style={styles.divider} />
          </Cell>
          <Cell size={4} style={styles.item}>
            <AcompanhamentoIconLabel icon='edema' iconText='Edema' childrenText={formatEdema(ultimasMedicoes.edema)} />
          </Cell>
          <Cell size={4} style={styles.item}>
            <AcompanhamentoIconLabel
              icon='glucometer'
              iconText='Glicemia capilar'
              childrenText={formatGlicemiaCapilar(ultimasMedicoes.glicemiaCapilar)}
            />
          </Cell>
          <Cell size={12} style={styles.dividerCell}>
            <Divider style={styles.divider} />
          </Cell>
          <Cell size={4} style={[styles.item, styles.lowerItem]}>
            <AcompanhamentoIconLabel
              icon='heartRateFilled'
              iconText='BCF 1'
              childrenText={formatMedicaoDefault(ultimasMedicoes.batimentoCardiacoFetal, 'bpm')}
            />
          </Cell>
          <Cell size={4} style={[styles.item, styles.lowerItem]}>
            <AcompanhamentoIconLabel
              icon='heartRateFilled'
              iconText='BCF 2'
              childrenText={formatMedicaoDefault(ultimasMedicoes.batimentoCardiacoFetal2, 'bpm')}
            />
          </Cell>
          <Cell size={4} style={[styles.item, styles.lowerItem]}>
            <AcompanhamentoIconLabel
              icon='exchange'
              iconText='Movimentação fetal'
              childrenText={formatMovimentacaoFetal(ultimasMedicoes.movimentacaoFetal)}
            />
          </Cell>
        </Grid>
      )}
    </AcompanhamentoCard>
  )
}

const createStyles = () => {
  return {
    item: css`
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    `,
    upperItem: css`
      padding-top: 1rem;
    `,
    lowerItem: css`
      padding-bottom: 1rem;
    `,
    dividerCell: css`
      padding: 0;
      margin: 0;
    `,
    divider: css`
      margin: 1rem 0.5rem;
    `,
  }
}
